import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import EditIcon from '@/components/Icons/EditIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import useFilter from '@/hooks/useFilter';
import { IDeleteResponse, IPageInfo } from '@/interfaces/common.model';
import { RootState } from '@/redux/store';
import {
  getFilterUrl,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AddWfhModal from './AddWFhModal';
import PlusIcon from '@/components/Icons/PlusIcon';
import { calculateDaysDifference } from '@/utility/CommonFunctions';
interface IPagination extends IPageInfo {
  count: number;
  searchQuery: string;
}
interface IWfhPageState {
  tableLoader: boolean;
  sortByDate: string[] | string;
}
interface IWfhState {
  wfhList: ITableData[];
  editData: ITableData | null;
}
interface IWfh {
  id: string;
  createdAt: string;
  startDate: string;
  endDate: string;
  visit: string;
  reason: string;
  days: number;
  wfhDuration: string;
  status: string;
}
interface IWfhResponse {
  status: boolean;
  data: {
    odRequest: IWfh[];
    pageInfo: IPageInfo;
  };
}
interface IModal {
  updateData: boolean;
  deleteData: boolean;
}
export interface ITableData {
  id: string;
  index: number;
  appliedDate: string;
  checkInType: string;
  checkInTime1: string;
  checkOutTime1: string;
  days: number;
  reasonOd: string;
  status: string;
  startDate?: string;
  endDate?: string;
  reason?: string;
  wfhDuration?: string;
}

const MyWfh = () => {
  const navigate = useNavigate();
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [requestData, setRequestData] = useState<ITableData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modal, setModal] = useState<IModal>({
    updateData: false,
    deleteData: false,
  });
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const [selectedId, setSelectedId] = useState<string>('');
  const [state, setState] = useState<IWfhState>({
    wfhList: [],
    editData: null,
  });
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: 1,
    pageSize: 10,
    count: 0,
    totalPages: 0,
    searchQuery: '',
  });
  const [pageStates, setPageStates] = useState<IWfhPageState>({
    tableLoader: false,
    sortByDate: [
      moment().startOf('month').format('YYYY-MM-DD'),
      moment().endOf('month').format('YYYY-MM-DD'),
    ],
  });

  const toggleModal = (type?: string) => {
    setModal((pre) => ({
      ...pre,
      ...(type === 'delete'
        ? { deleteData: !pre.deleteData }
        : { updateData: !pre.updateData }),
    }));
  };

  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'appliedDate', label: 'Applied Date' },
    // { value: 'checkInType', label: 'Check-In Type' },
    { value: 'checkInTime1', label: 'Start Date' },
    { value: 'checkOutTime1', label: 'End Date' },
    { value: 'days', label: 'Days' },
    // { value: 'reasonOd', label: 'Reason' },
    { value: 'status', label: 'Status' },
  ];

  useEffect(() => {
    getMyWfhRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageStates?.sortByDate,
    pagination?.searchQuery,
    pagination.currentPage,
    filterDetails.pageSize,
    filterDetails?.orderBy,
  ]);

  const getMyWfhRequest = async () => {
    try {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: !pre.tableLoader,
      }));
      const filter = {
        pageNumber: pagination?.currentPage,
        pageSize: filterDetails?.pageSize,
        startDate: pageStates?.sortByDate[0],
        endDate: pageStates?.sortByDate[1],
        search: pagination.searchQuery,
        orderBy: filterDetails?.orderBy,
      };
      const API =
        API_URL +
        API_ENDPOINT?.getWfh +
        '?' +
        getFilterUrl(filter) +
        '&myOdrequest=true';

      const response: IWfhResponse = await callApi(API, {
        method: 'GET',
      });
      if (response?.status === true) {
        const newData = response?.data?.odRequest?.map((item, index) => {
          const days =
            Number(
              calculateDaysDifference(item?.startDate, item?.endDate, 'number'),
            ) || 0;
          const result =
            item?.wfhDuration == 'firstHalf' ||
            item?.wfhDuration == 'secondHalf'
              ? days * 0.5
              : days;
          return {
            ...item,
            id: item?.id,
            index: index + 1,
            days: result ?? '--',
            appliedDate: moment(item?.createdAt).format('DD/MM/YYYY'),
            checkInTime1: moment(item?.startDate).format('DD-MM-YYYY'),
            checkOutTime1: moment(item?.endDate).format('DD-MM-YYYY'),
            checkInType: item?.visit,
            reasonOd: item?.reason,
            status: item?.status,
          };
        });

        setState((pre) => ({
          ...pre,
          wfhList: newData,
        }));
        setPagination((pre) => ({
          ...pre,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: false,
      }));
    }
  };

  const dateChangeHandler = (Dates: Date[]) => {
    let formattedDate = null;
    if (Dates && Dates.length === 2) {
      const startDate = new Date(
        Dates[0].getTime() - Dates[0].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      const endDate = new Date(
        Dates[1].getTime() - Dates[1].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      formattedDate = [startDate, endDate];
    }
    if (formattedDate !== null) {
      setPageStates((prevState) => ({
        ...prevState,
        sortByDate: formattedDate,
      }));
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
      }));
    } else {
      setPageStates((prevState) => ({ ...prevState, sortByDate: '' }));
    }
  };

  const deleteHandler = (data: ITableData) => {
    setRequestData(data);
    setSelectedId(data?.id);
    toggleModal('delete');
  };

  const handleDelete = async () => {
    const isHalfDay =
      requestData?.wfhDuration == 'secondHalf' ||
      requestData?.wfhDuration == 'firstHalf';
    try {
      setIsLoading(true);
      const API =
        API_URL +
        API_ENDPOINT?.deleteWfh +
        `?wfhId=${selectedId}&type=${isHalfDay ? 'halfDayOD' : 'odRequest'}`;
      const response: IDeleteResponse = await callApi(API, {
        method: 'GET',
      });
      if (response?.status === true) {
        showToastNotification('success', response?.message);
        toggleModal('delete');
        getMyWfhRequest();
      } else {
        showToastNotification('danger', response?.message);
        toggleModal('delete');
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const ActionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view',
      isShow: 'always',
      clickHandler: (data: ITableData) =>
        navigate(`${RoutesLink?.wfhRequestDetail}/${data?.id}`),
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      // isShow: 0,
      isHidden: (data: ITableData) =>
        Number(data.status) === 1 || Number(data.status) === 2,
      clickHandler: (data: ITableData) => {
        setState((prev) => ({ ...prev, editData: data }));
        toggleModal();
      },
    },
    {
      label: 'Delete',
      className: 'btn btn-link',
      icon: <DeleteIcon />,
      title: 'delete',
      isShow: 0,
      clickHandler: deleteHandler,
    },
  ];

  const { handleSearchChange, handleChangePageSize, orderByHandler } =
    useFilter(pagination, setPagination);

  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Work From Home Request'}
          buttonIcon={<PlusIcon />}
          buttonText={'Apply WFH Request'}
          clickHandler={() => {
            setState((prev) => ({ ...prev, editData: null }));
            toggleModal();
          }}
          dateChangeHandler={dateChangeHandler}
          showDefaultDate={true}
        />
        <CardLayout cardStyle="mb-4" isGrid={tableView === 'grid'}>
          <FilterComponent
            orderByList={orderByList}
            searchPlaceHolder="Search"
            handleSearch={handleSearchChange}
            orderByHandler={orderByHandler}
            isGrid={false}
          />
          <CommonTable
            tableHeading={tableHeading}
            tableData={state?.wfhList}
            ActionData={ActionData}
            currentPage={pagination?.currentPage}
            pageSize={filterDetails?.pageSize}
            totalPages={pagination?.totalPages}
            count={pagination?.count}
            isLoading={pageStates?.tableLoader}
            handleChangePageSize={handleChangePageSize}
          />
        </CardLayout>
      </div>
      <AddWfhModal
        isOpen={modal.updateData}
        editData={state.editData}
        onClose={() => {
          toggleModal();
          setState((prev) => ({ ...prev, editData: null }));
        }}
        getMyWfhRequest={getMyWfhRequest}
      />
      <ConfirmModal
        isOpen={modal?.deleteData}
        toggle={() => toggleModal('delete')}
        submitHandler={handleDelete}
        isLoading={isLoading}
      />
    </Layout>
  );
};

export default MyWfh;
